import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import "./procedure-record-manager.css";

type ProcedureRecord = {
  id:string;
  document_type:"WPS"|"WPDS";
  document_number:string;
  title:string|null;
  process:string;
  governing_standard:string|null;
  revision:string|null;
  status:string;
  accepted_date:string|null;
  material_division?:string|null;
  qualification_scope?:string|null;
  process_mode?:string|null;
  joint_type?:string|null;
  filler_classification?:string|null;
  document_path?:string|null;
  uploaded_filename?:string|null;
  qualified_positions?:string[]|null;
  base_metal_group?:string|null;
  thickness_min_mm?:number|null;
  thickness_max_mm?:number|null;
  search_tags?:string[]|null;
};

const POSITION_CHOICES=["Flat","Horizontal","Vertical","Overhead"];
const procedureTagChoices=[
  "SMAW","FCAW","MCAW","GMAW","GTAW","SAW","PAW",
  "FW","Fillet weld","Groove","CJP","PJP","Tack weld","TW","Flare bevel","Flare vee",
  "Flat","Horizontal","Vertical","Overhead","All position",
  "Manual","Semi-automatic","Mechanized","Automatic","Robotic",
  "W47.1","W47.2","W59","W59.2","Annex J","Annex K","Annex M",
  "Carbon steel","Stainless steel","Aluminum","Marine"
];
const unique=(values:(string|null|undefined)[])=>Array.from(new Set(values.filter(Boolean).map(v=>String(v).trim()).filter(Boolean)));
const automaticProcedureTags=(record:ProcedureRecord)=>unique([
  record.document_type,
  ...record.process.split(/[\/,+]/).map(x=>x.trim()),
  record.process_mode,
  record.joint_type,
  record.material_division?.toUpperCase(),
  record.governing_standard,
  record.qualification_scope==="annex_m"?"Annex M":record.qualification_scope==="annex_k"?"Annex K":record.qualification_scope==="annex_j"?"Annex J":null,
  record.base_metal_group,
  record.filler_classification,
  ...(record.qualified_positions||[]),
  /fillet/i.test(record.joint_type||"")?"FW":null,
  /tack/i.test(record.joint_type||"")?"TW":null,
  /groove/i.test(record.joint_type||"")?"Groove":null
]);

function readPreviewRecord(documentNumber:string):ProcedureRecord|null {
  try {
    const raw=window.localStorage.getItem("arcvault-preview-procedures");
    if(!raw)return null;
    const records=JSON.parse(raw) as ProcedureRecord[];
    return records.find(x=>x.document_number===documentNumber)||null;
  } catch { return null; }
}

function writePreviewRecord(record:ProcedureRecord){
  const raw=window.localStorage.getItem("arcvault-preview-procedures");
  const records=raw?JSON.parse(raw) as ProcedureRecord[]:[];
  window.localStorage.setItem("arcvault-preview-procedures",JSON.stringify(records.map(x=>x.id===record.id?record:x)));
}

function deletePreviewRecord(id:string){
  const raw=window.localStorage.getItem("arcvault-preview-procedures");
  const records=raw?JSON.parse(raw) as ProcedureRecord[]:[];
  window.localStorage.setItem("arcvault-preview-procedures",JSON.stringify(records.filter(x=>x.id!==id)));
}

export default function ProcedureRecordManager(){
  const [record,setRecord]=useState<ProcedureRecord|null>(null);
  const [draft,setDraft]=useState<ProcedureRecord|null>(null);
  const [editing,setEditing]=useState(false);
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [confirmDelete,setConfirmDelete]=useState(false);
  const [error,setError]=useState("");
  const [isPreview,setIsPreview]=useState(false);

  useEffect(()=>{
    async function onClick(event:MouseEvent){
      const target=event.target as HTMLElement;
      if(target.closest("button,a,input,select,textarea"))return;
      const recordElement=target.closest<HTMLElement>("[data-procedure-number], .procedure-table tbody tr");
      if(!recordElement)return;
      const documentNumber=recordElement.dataset.procedureNumber||recordElement.querySelector("td:first-child b")?.textContent?.trim();
      if(!documentNumber)return;

      setLoading(true);setError("");setConfirmDelete(false);setEditing(false);
      const previewRecord=readPreviewRecord(documentNumber);
      if(previewRecord){setIsPreview(true);setRecord(previewRecord);setDraft(previewRecord);setLoading(false);return;}

      setIsPreview(false);
      const {data,error}=await supabase.from("procedures").select("*").eq("document_number",documentNumber).limit(1);
      setLoading(false);
      if(error||!data?.length){setError("ArcVault could not load this procedure record.");return;}
      const next=data[0] as ProcedureRecord;
      setRecord(next);setDraft(next);
    }
    document.addEventListener("click",onClick,true);
    return()=>document.removeEventListener("click",onClick,true);
  },[]);

  const application=useMemo(()=>{
    if(!record)return "";
    return [record.process_mode,record.joint_type,record.filler_classification].filter(Boolean).join(" · ");
  },[record]);
  const shownTags=useMemo(()=>record?unique([...automaticProcedureTags(record),...(record.search_tags||[])]):[],[record]);
  const suggestedTags=useMemo(()=>draft?unique([...automaticProcedureTags(draft),...procedureTagChoices]):procedureTagChoices,[draft]);

  function close(){setRecord(null);setDraft(null);setEditing(false);setConfirmDelete(false);setError("");}
  function cancelEdit(){setDraft(record);setEditing(false);setError("");}
  function toggleTag(tag:string){if(!draft)return;const current=draft.search_tags||[];setDraft({...draft,search_tags:current.includes(tag)?current.filter(x=>x!==tag):[...current,tag]});}
  function togglePosition(position:string){
    if(!draft)return;
    const current=draft.qualified_positions||[];
    setDraft({...draft,qualified_positions:current.includes(position)?current.filter(x=>x!==position):[...current,position]});
  }
  function openOfficialDocument(){
    if(!record)return;
    const elements=Array.from(document.querySelectorAll<HTMLElement>("[data-procedure-number], .procedure-table tbody tr"));
    const element=elements.find(el=>el.dataset.procedureNumber===record.document_number||el.querySelector("td:first-child b")?.textContent?.trim()===record.document_number);
    element?.querySelector<HTMLButtonElement>(".pdf-button")?.click();
  }

  async function save(e:FormEvent){
    e.preventDefault();
    if(!record||!draft)return;
    if(!draft.document_number.trim()){setError("Document number is required.");return;}
    if(!draft.process.trim()){setError("Welding process is required.");return;}
    if(draft.document_type==="WPDS"&&!(draft.qualified_positions||[]).length){setError("Select at least one qualified welding position for this WPDS.");return;}
    if(draft.document_type==="WPDS"&&draft.thickness_min_mm!=null&&draft.thickness_max_mm!=null&&draft.thickness_min_mm>draft.thickness_max_mm){setError("Minimum WPDS thickness cannot be greater than maximum thickness.");return;}
    setSaving(true);setError("");
    const mergedTags=unique([...(draft.search_tags||[]),...automaticProcedureTags(draft)]);
    const nextDraft={...draft,search_tags:mergedTags};
    if(isPreview){writePreviewRecord(nextDraft);setRecord(nextDraft);setDraft(nextDraft);setEditing(false);setSaving(false);window.location.reload();return;}
    const payload={
      document_number:nextDraft.document_number.trim(),title:nextDraft.title?.trim()||null,process:nextDraft.process.trim(),
      governing_standard:nextDraft.governing_standard?.trim()||null,revision:nextDraft.revision?.trim()||null,status:nextDraft.status,
      accepted_date:nextDraft.accepted_date||null,process_mode:nextDraft.process_mode?.trim()||null,joint_type:nextDraft.joint_type?.trim()||null,
      filler_classification:nextDraft.filler_classification?.trim()||null,
      thickness_min_mm:nextDraft.document_type==="WPDS"?(nextDraft.thickness_min_mm??null):nextDraft.thickness_min_mm,
      thickness_max_mm:nextDraft.document_type==="WPDS"?(nextDraft.thickness_max_mm??null):nextDraft.thickness_max_mm,
      qualified_positions:nextDraft.document_type==="WPDS"?(nextDraft.qualified_positions||[]):nextDraft.qualified_positions,
      search_tags:mergedTags
    };
    const {data,error}=await supabase.from("procedures").update(payload).eq("id",record.id).select("*").single();
    setSaving(false);
    if(error||!data){setError("ArcVault could not save these procedure changes.");return;}
    setRecord(data as ProcedureRecord);setDraft(data as ProcedureRecord);setEditing(false);window.location.reload();
  }

  async function remove(){
    if(!record)return;
    setSaving(true);setError("");
    if(isPreview){deletePreviewRecord(record.id);setSaving(false);close();window.location.reload();return;}
    const {error}=await supabase.from("procedures").delete().eq("id",record.id);
    setSaving(false);
    if(error){setError("ArcVault could not delete this procedure record.");return;}
    close();window.location.reload();
  }

  if(!record&&!loading&&!error)return null;

  return <div className="procedure-record-overlay" role="dialog" aria-modal="true" onMouseDown={e=>{if(e.target===e.currentTarget)close()}}>
    <section className="procedure-record-panel">
      <button className="procedure-record-close" onClick={close} aria-label="Close">×</button>
      {loading?<div className="procedure-record-loading">Loading procedure…</div>:record&&draft?<>
        <header className="procedure-record-header">
          <div><span className="eyebrow">CONTROLLED PROCEDURE RECORD</span><h2>{record.document_number}</h2><p>{record.title||"Untitled procedure"}</p></div>
          <span className={`status ${record.status==="active"?"active":record.status==="pending"?"expiring":"expired"}`}>● {record.status.toUpperCase()}</span>
        </header>

        {!editing?<>
          <div className="procedure-record-facts">
            <div><small>TYPE</small><b>{record.document_type}</b></div>
            <div><small>PROCESS</small><b>{record.process}</b></div>
            <div><small>REVISION</small><b>{record.revision||"—"}</b></div>
            <div><small>ACCEPTED</small><b>{record.accepted_date||"Pending"}</b></div>
            <div><small>STANDARD / SCOPE</small><b>{record.governing_standard||"—"}</b></div>
            <div><small>DIVISION</small><b>{record.material_division?.toUpperCase()||"—"}</b></div>
          </div>
          {record.document_type==="WPDS"?<><div className="procedure-position-summary"><small>QUALIFIED POSITIONS</small><div>{(record.qualified_positions||[]).length?(record.qualified_positions||[]).map(position=><span key={position}>{position}</span>):<em>Positions not entered</em>}</div></div><div className="procedure-record-application"><small>WPDS THICKNESS RANGE</small><p>{record.thickness_min_mm!=null||record.thickness_max_mm!=null?`${record.thickness_min_mm??"—"} mm to ${record.thickness_max_mm??"and above"}`:"Thickness range not entered"}</p></div></>:null}
          {application?<div className="procedure-record-application"><small>APPLICATION</small><p>{application}</p></div>:null}
          <div className="record-tags"><small>SEARCH TAGS</small><div>{shownTags.map(tag=><span key={tag}>{tag}</span>)}</div></div>
          <div className="procedure-record-actions">
            {record.document_path?<button onClick={openOfficialDocument}>View official document</button>:null}
            <button className="primary" onClick={()=>setEditing(true)}>Edit record</button>
            <button className="danger-button" onClick={()=>setConfirmDelete(true)}>Delete record</button>
            <button onClick={close}>Close</button>
          </div>
          {confirmDelete?<div className="procedure-delete-confirm"><b>Delete {record.document_number}?</b><p>This removes the ArcVault procedure record. The uploaded controlled file is not automatically removed from storage.</p><div><button onClick={()=>setConfirmDelete(false)}>Cancel</button><button className="danger-button" disabled={saving} onClick={remove}>{saving?"Deleting…":"Delete record"}</button></div></div>:null}
        </>:<form onSubmit={save} className="procedure-record-form">
          <div className="procedure-record-grid">
            <label>Document number<input value={draft.document_number} onChange={e=>setDraft({...draft,document_number:e.target.value})}/></label>
            <label>Title<input value={draft.title||""} onChange={e=>setDraft({...draft,title:e.target.value})}/></label>
            <label>Process<input value={draft.process} onChange={e=>setDraft({...draft,process:e.target.value})}/></label>
            <label>Revision<input value={draft.revision||""} onChange={e=>setDraft({...draft,revision:e.target.value})}/></label>
            <label>Accepted date<input type="date" value={draft.accepted_date||""} onChange={e=>setDraft({...draft,accepted_date:e.target.value})}/></label>
            <label>Status<select value={draft.status} onChange={e=>setDraft({...draft,status:e.target.value})}><option value="active">Active</option><option value="pending">Pending</option><option value="superseded">Superseded</option></select></label>
            <label>Governing standard<input value={draft.governing_standard||""} onChange={e=>setDraft({...draft,governing_standard:e.target.value})}/></label>
            <label>Process mode<input value={draft.process_mode||""} onChange={e=>setDraft({...draft,process_mode:e.target.value})}/></label>
            <label>Joint type<input value={draft.joint_type||""} onChange={e=>setDraft({...draft,joint_type:e.target.value})}/></label>
            <label>Filler classification<input value={draft.filler_classification||""} onChange={e=>setDraft({...draft,filler_classification:e.target.value})}/></label>
            {draft.document_type==="WPDS"?<><label>Minimum thickness (mm)<input type="number" min="0" step="0.1" value={draft.thickness_min_mm??""} onChange={e=>setDraft({...draft,thickness_min_mm:e.target.value===""?null:Number(e.target.value)})}/></label><label>Maximum thickness (mm)<input type="number" min="0" step="0.1" value={draft.thickness_max_mm??""} onChange={e=>setDraft({...draft,thickness_max_mm:e.target.value===""?null:Number(e.target.value)})} placeholder="Leave blank if unlimited"/></label></>:null}
          </div>
          {draft.document_type==="WPDS"?<div className="procedure-position-editor"><div><b>Qualified welding positions</b><small>Select the production positions covered by this WPDS. These positions also become automatic search tags and are used by the Welder / WPDS Check.</small></div><div className="procedure-position-picker">{POSITION_CHOICES.map(position=>{const active=(draft.qualified_positions||[]).includes(position);return <button type="button" key={position} className={active?"active":""} onClick={()=>togglePosition(position)}>{active?"✓ ":""}{position}</button>})}</div></div>:null}
          <div className="tag-editor"><div><b>Search tags</b><small>ArcVault automatically adds tags from the process, joint, scope and positions. Add any useful shop-search terms below.</small></div><div className="tag-picker">{suggestedTags.map(tag=>{const automatic=automaticProcedureTags(draft).includes(tag);const active=automatic||(draft.search_tags||[]).includes(tag);return <button type="button" key={tag} className={active?"active":""} disabled={automatic} onClick={()=>toggleTag(tag)}>{tag}{automatic?" · AUTO":""}</button>})}</div></div>
          {error?<div className="form-message">{error}</div>:null}
          <div className="procedure-record-actions"><button type="button" onClick={cancelEdit}>Cancel</button><button className="primary" disabled={saving}>{saving?"Saving…":"Save changes"}</button></div>
        </form>}
      </>:<div className="form-message">{error}<div className="procedure-record-actions"><button onClick={close}>Close</button></div></div>}
    </section>
  </div>;
}
